package com.evoapps.evoshift.data.dto.weather

import com.evoapps.evoshift.domain.model.DayWeather
import com.evoapps.evoshift.domain.model.WeatherCondition
import com.evoapps.evoshift.domain.model.WeatherSource
import com.evoapps.evoshift.domain.model.WeatherSummary
import kotlinx.datetime.LocalDate
import kotlinx.serialization.Serializable

/**
 * `workplaces/{id}/weather/{YYYY-MM-DD}` (SH-226) as functions/weather.js writes it: the forecast
 * (merged every ~6 hours) and what was measured (merged the next morning). Every field optional:
 * the app reads what is there and ignores what it does not know.
 */
@Serializable
data class WeatherDayDto(
    val date: String? = null,
    val forecast: WeatherSummaryDto? = null,
    val observed: WeatherSummaryDto? = null
) {
    /** The domain day, or null when the date is not a date. */
    fun toDomain(fallbackDate: String? = null): DayWeather? {
        val day = runCatching { LocalDate.parse(date ?: fallbackDate ?: return null) }.getOrNull() ?: return null
        return DayWeather(day, forecast?.toDomain(), observed?.toDomain())
    }
}

@Serializable
data class WeatherSummaryDto(
    val minC: Double? = null,
    val maxC: Double? = null,
    val precipitationMm: Double? = null,
    val precipitationProbability: Double? = null,
    val condition: String? = null,
    val windMs: Double? = null,
    val source: String? = null,
    val stationName: String? = null,
    val distanceKm: Double? = null
) {
    /** Null without both temperatures (nothing worth showing). */
    fun toDomain(): WeatherSummary? {
        val min = minC ?: return null
        val max = maxC ?: return null
        if (!min.isFinite() || !max.isFinite()) return null
        return WeatherSummary(
            minC = min,
            maxC = max,
            condition = WeatherCondition.fromName(condition),
            precipitationMm = precipitationMm?.takeIf { it.isFinite() && it >= 0 },
            precipitationProbability = precipitationProbability?.takeIf { it.isFinite() }?.toInt()?.coerceIn(0, 100),
            windMs = windMs?.takeIf { it.isFinite() && it >= 0 },
            source = WeatherSource.fromName(source),
            stationName = stationName,
            distanceKm = distanceKm
        )
    }
}
